import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Download,
  Search,
  Check,
  X,
  CircleDot,
  ChevronRight,
  Mail,
  Phone,
  Link as LinkIcon,
  FileText,
  Linkedin,
  Send,
  MailCheck,
} from "lucide-react";
import {
  fetchApplications,
  patchApplication,
  downloadCsv,
} from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { useOutletContext } from "react-router-dom";

const STAGES = ["new", "shortlisted", "interview", "offer", "hired", "rejected"];
const STAGE_COLORS = {
  new: "bg-ink text-white",
  shortlisted: "bg-amber-100 text-amber-900 border border-amber-300",
  interview: "bg-blue-100 text-blue-900 border border-blue-300",
  offer: "bg-emerald-100 text-emerald-900 border border-emerald-300",
  hired: "bg-vermilion text-white",
  rejected: "bg-muted text-ink/60 border border-border",
};

function fmt(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function errToString(d) {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

export default function Applications() {
  const [params, setParams] = useSearchParams();
  const { stats, reloadStats } = useOutletContext() || {};
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [notifyOnStageChange, setNotifyOnStageChange] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");

  const filters = useMemo(
    () => ({
      job_id: params.get("job_id") || undefined,
      stage: params.get("stage") || undefined,
      unread: params.get("unread") === "true" ? true : undefined,
      q: params.get("q") || undefined,
      from: params.get("from") || undefined,
      to: params.get("to") || undefined,
    }),
    [params]
  );

  const load = () => {
    setLoading(true);
    fetchApplications(filters)
      .then(setList)
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === undefined || v === "" || v === null || v === "all") next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
  };

  const onSearch = (e) => {
    e.preventDefault();
    setFilter("q", q.trim() || undefined);
  };

  const openRow = async (row) => {
    setActive(row);
    if (!row.read) {
      try {
        const updated = await patchApplication(row.id, { read: true });
        setList((xs) => xs.map((x) => (x.id === row.id ? updated : x)));
        setActive(updated);
        reloadStats?.();
      } catch {/* ignore */}
    }
  };

  const changeStage = async (row, stage) => {
    try {
      const willNotify = notifyOnStageChange && row.stage !== stage;
      const updated = await patchApplication(row.id, {
        stage,
        notify_candidate: willNotify,
      });
      setList((xs) => xs.map((x) => (x.id === row.id ? updated : x)));
      if (active?.id === row.id) setActive(updated);
      reloadStats?.();
      if (willNotify) {
        if (updated.last_notified_sent) {
          toast.success(`Moved to ${stage} — candidate notified.`);
        } else {
          toast.message(
            `Moved to ${stage}. Email to candidate could not be sent (check Resend sender/domain).`
          );
        }
      } else {
        toast.success(`Moved to ${stage}.`);
      }
    } catch (err) {
      toast.error(errToString(err?.response?.data?.detail));
    }
  };

  const addNote = async (row, body) => {
    try {
      const updated = await patchApplication(row.id, { note: body });
      setList((xs) => xs.map((x) => (x.id === row.id ? updated : x)));
      setActive(updated);
      toast.success("Note added.");
    } catch (err) {
      toast.error(errToString(err?.response?.data?.detail));
    }
  };

  const toggleRead = async (row) => {
    try {
      const updated = await patchApplication(row.id, { read: !row.read });
      setList((xs) => xs.map((x) => (x.id === row.id ? updated : x)));
      if (active?.id === row.id) setActive(updated);
      reloadStats?.();
    } catch (err) {
      toast.error(errToString(err?.response?.data?.detail));
    }
  };

  const jobs = stats?.applications?.by_job || [];

  return (
    <div className="p-6 md:p-10" data-testid="admin-applications">
      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline text-vermilion mb-3">Pipeline</div>
          <h1 className="font-serif text-3xl md:text-4xl tracking-tight">
            Applications{" "}
            <span className="text-ink/40 font-mono text-sm align-middle">
              {loading ? "…" : `${list.length}`}
            </span>
          </h1>
        </div>
        <button
          onClick={() => downloadCsv("applications", filters)}
          data-testid="apps-export"
          className="btn-outline text-[11px]"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </header>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-8">
        <form onSubmit={onSearch} className="md:col-span-4 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, cover…"
            data-testid="apps-search"
            className="pl-9 h-11 rounded-none border-ink/20"
          />
        </form>
        <div className="md:col-span-3">
          <Select
            value={filters.job_id ?? "all"}
            onValueChange={(v) => setFilter("job_id", v === "all" ? undefined : v)}
          >
            <SelectTrigger data-testid="apps-filter-job" className="rounded-none h-11 border-ink/20">
              <SelectValue placeholder="All roles" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All roles</SelectItem>
              {jobs.map((j) => (
                <SelectItem key={j.job_id} value={j.job_id}>
                  {j.title} · {j.level}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Select
            value={filters.stage ?? "all"}
            onValueChange={(v) => setFilter("stage", v === "all" ? undefined : v)}
          >
            <SelectTrigger data-testid="apps-filter-stage" className="rounded-none h-11 border-ink/20 capitalize">
              <SelectValue placeholder="All stages" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All stages</SelectItem>
              {STAGES.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3 flex gap-2">
          <button
            onClick={() => setFilter("unread", filters.unread ? undefined : "true")}
            data-testid="apps-filter-unread"
            className={`flex-1 h-11 border text-xs uppercase tracking-[0.18em] flex items-center justify-center gap-2 transition-colors ${
              filters.unread
                ? "bg-vermilion text-white border-vermilion"
                : "border-ink/20 text-ink hover:bg-muted"
            }`}
          >
            <CircleDot className="w-3.5 h-3.5" /> Unread
          </button>
          <button
            onClick={() => setParams({})}
            className="h-11 px-4 border border-ink/20 text-xs uppercase tracking-[0.18em] text-ink/70 hover:bg-muted"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="border-t border-border" data-testid="apps-list">
        <div className="grid grid-cols-12 gap-4 py-3 overline text-ink/40 border-b border-border px-2">
          <div className="col-span-4">Candidate</div>
          <div className="col-span-3">Role</div>
          <div className="col-span-2">Stage</div>
          <div className="col-span-2">Received</div>
          <div className="col-span-1 text-right"> </div>
        </div>

        {loading && (
          <div className="py-20 text-center text-ink/50">Loading…</div>
        )}
        {!loading && list.length === 0 && (
          <div className="py-20 text-center text-ink/50">
            No applications match these filters.
          </div>
        )}

        <ul>
          {list.map((row) => (
            <li
              key={row.id}
              data-testid={`app-row-${row.id}`}
              className={`grid grid-cols-12 gap-4 py-5 px-2 border-b border-border items-center cursor-pointer hover:bg-muted/60 transition-colors ${
                !row.read ? "bg-vermilion/[0.03]" : ""
              }`}
              onClick={() => openRow(row)}
            >
              <div className="col-span-4 min-w-0 flex items-center gap-3">
                {!row.read && (
                  <span className="w-2 h-2 rounded-full bg-vermilion flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="font-serif text-lg truncate">{row.full_name}</div>
                  <div className="text-xs text-ink/50 truncate">{row.email}</div>
                </div>
              </div>
              <div className="col-span-3 text-sm truncate">
                <div className="text-ink truncate">{row.job_title || row.job_id}</div>
                <div className="text-xs text-ink/50 truncate">{row.job_level || ""}</div>
              </div>
              <div className="col-span-2">
                <span
                  className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold ${
                    STAGE_COLORS[row.stage] || STAGE_COLORS.new
                  }`}
                >
                  {row.stage}
                </span>
              </div>
              <div className="col-span-2 text-xs text-ink/60">
                {fmt(row.created_at)}
              </div>
              <div className="col-span-1 flex justify-end">
                <ChevronRight className="w-4 h-4 text-ink/40" />
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Detail drawer */}
      <Sheet
        open={!!active}
        onOpenChange={(v) => {
          if (!v) setActive(null);
        }}
      >
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl overflow-y-auto rounded-none border-l border-border p-0"
          data-testid="app-detail"
        >
          {active && (
            <div>
              <SheetHeader className="p-8 border-b border-border">
                <div className="overline text-vermilion mb-2">
                  {active.job_title || active.job_id}{" "}
                  {active.job_level ? `· ${active.job_level}` : ""}
                </div>
                <SheetTitle className="font-serif text-3xl md:text-4xl leading-[1.05] tracking-tight">
                  {active.full_name}
                </SheetTitle>
                <SheetDescription className="text-sm text-ink/60 mt-1">
                  Received {fmt(active.created_at)}
                </SheetDescription>
              </SheetHeader>

              {/* Stage buttons */}
              <div className="p-8 border-b border-border">
                <div className="flex items-center justify-between gap-4 mb-3">
                  <div className="overline text-ink/60">Stage</div>
                  <label
                    className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] cursor-pointer select-none"
                    data-testid="notify-toggle-label"
                  >
                    <input
                      type="checkbox"
                      checked={notifyOnStageChange}
                      onChange={(e) => setNotifyOnStageChange(e.target.checked)}
                      data-testid="notify-toggle"
                      className="w-3.5 h-3.5 accent-vermilion"
                    />
                    <Send className="w-3.5 h-3.5" /> Notify candidate
                  </label>
                </div>
                <div className="flex flex-wrap gap-2">
                  {STAGES.map((s) => (
                    <button
                      key={s}
                      onClick={() => changeStage(active, s)}
                      data-testid={`stage-btn-${s}`}
                      className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold capitalize transition-colors ${
                        active.stage === s
                          ? STAGE_COLORS[s]
                          : "border border-ink/20 text-ink/60 hover:bg-muted"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {active.last_notified_stage && (
                  <div
                    className="mt-4 flex items-center gap-2 text-xs text-ink/60"
                    data-testid="last-notified"
                  >
                    <MailCheck
                      className={`w-3.5 h-3.5 ${
                        active.last_notified_sent ? "text-vermilion" : "text-ink/40"
                      }`}
                    />
                    Last notified candidate:{" "}
                    <span className="capitalize text-ink">
                      {active.last_notified_stage}
                    </span>
                    {active.last_notified_at && (
                      <span className="text-ink/40">
                        · {new Date(active.last_notified_at).toLocaleString()}
                      </span>
                    )}
                    {!active.last_notified_sent && (
                      <span className="text-vermilion">(send failed)</span>
                    )}
                  </div>
                )}
                <button
                  onClick={() => toggleRead(active)}
                  data-testid="toggle-read"
                  className="mt-4 text-xs uppercase tracking-[0.18em] text-ink/50 inline-flex items-center gap-2 hover:text-ink"
                >
                  {active.read ? (
                    <>
                      <X className="w-3.5 h-3.5" /> Mark as unread
                    </>
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5" /> Mark as read
                    </>
                  )}
                </button>
              </div>

              {/* Candidate info */}
              <div className="p-8 border-b border-border space-y-3 text-sm">
                <InfoRow icon={Mail} label="Email" value={<a className="link-underline" href={`mailto:${active.email}`}>{active.email}</a>} />
                <InfoRow icon={Phone} label="Phone" value={active.phone || "—"} />
                <InfoRow
                  icon={Linkedin}
                  label="LinkedIn"
                  value={active.linkedin_url ? <a className="link-underline" href={active.linkedin_url} target="_blank" rel="noreferrer">{active.linkedin_url}</a> : "—"}
                />
                <InfoRow
                  icon={LinkIcon}
                  label="Portfolio"
                  value={active.portfolio_url ? <a className="link-underline" href={active.portfolio_url} target="_blank" rel="noreferrer">{active.portfolio_url}</a> : "—"}
                />
                <InfoRow
                  icon={FileText}
                  label="Resume"
                  value={active.resume_url ? <a className="link-underline" href={active.resume_url} target="_blank" rel="noreferrer">{active.resume_url}</a> : "—"}
                />
                <InfoRow icon={CircleDot} label="Years of exp" value={active.years_experience || "—"} />
              </div>

              {/* Cover */}
              {active.cover_letter && (
                <div className="p-8 border-b border-border">
                  <div className="overline text-ink/60 mb-2">Cover note</div>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink">
                    {active.cover_letter}
                  </p>
                </div>
              )}

              {/* Notes */}
              <NotesPanel
                key={`notes-${active.id}-${(active.notes || []).length}`}
                notes={active.notes || []}
                onAdd={(body) => addNote(active, body)}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="grid grid-cols-12 gap-3 items-start">
      <div className="col-span-4 flex items-center gap-2 text-ink/50">
        <Icon className="w-3.5 h-3.5" />
        <span className="overline">{label}</span>
      </div>
      <div className="col-span-8 text-ink break-words">{value}</div>
    </div>
  );
}

export function NotesPanel({ notes, onAdd }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const t = body.trim();
    if (!t) return;
    setBusy(true);
    try {
      await onAdd(t);
      setBody("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8">
      <div className="overline text-ink/60 mb-4">Internal notes</div>
      <ul className="space-y-4 mb-6">
        {(notes || []).length === 0 && (
          <li className="text-sm text-ink/40">
            No notes yet. Leave a short call summary or screening outcome.
          </li>
        )}
        {(notes || []).map((n, i) => (
          <li key={i} className="border-l-2 border-vermilion/60 pl-4 py-1">
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{n.body}</p>
            <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-ink/50">
              {n.author} · {fmt(n.created_at)}
            </div>
          </li>
        ))}
      </ul>

      <form onSubmit={submit} className="space-y-3">
        <Label htmlFor="note" className="overline text-ink/60">
          Add a note
        </Label>
        <Textarea
          id="note"
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Rang the candidate, very articulate. Next: set up panel."
          data-testid="note-input"
          className="rounded-none border-ink/20 focus-visible:ring-ink/30 resize-none"
        />
        <button
          type="submit"
          disabled={busy || !body.trim()}
          data-testid="note-submit"
          className="btn-primary disabled:opacity-60"
        >
          {busy ? "Saving…" : "Save note"}
        </button>
      </form>
    </div>
  );
}
